import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Field, FieldArray } from 'redux-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { FieldError } from '@/ui';
import NativeSelectField from '../FormFields/NativeSelectField';
import { validateWholeNums } from '../../views/Offers/OfferTitle/valildation';
import { renderCriteriaOptions, renderField, renderMultiSelect } from '../FormFields/reduxFormRenderers';
import { sequenceDetailsSelector } from '../../views/Sequences/actions/reducers';
import { getFormSelector } from '../../reducers/FormSelectorReducer';

const renderAndOrSelect = ({ input, meta: { error, touched } }) => (
  <div>
    <NativeSelectField {...input} value={input.value || ''} error={Boolean(error && touched)}>
      <option value=''>Select Logic</option>
      <option value='AND'>All entries must pass</option>
      <option value='OR'>Any of these entries can pass</option>
    </NativeSelectField>
    <FieldError error={error} touched={touched} />
  </div>
);

const renderRegistrationFields = ({
  fields,
  andOrLogicFieldVal,
  domainsForPreviousRegReq,
  affiliatesForPreviousRegReq,
  isLoading,
}) => {
  useEffect(() => {
    if (fields.length === 0) {
      fields.push({
        domains: [],
        affiliates: [],
        includeExcludeAction: null,
        previousRegistrationsMin: null,
        previousRegistrationsMax: null,
      });
    }
  }, [fields]);

  if (!['AND', 'OR'].includes(andOrLogicFieldVal)) {
    return null;
  }

  return (
    <div>
      {fields.map((fieldName, index) => (
        <Box key={index} sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ alignItems: 'start' }}>
            {fields.length > 1 && (
              <Grid size={1}>
                <Tooltip title='Remove Previous Registration'>
                  <IconButton
                    color='error'
                    aria-label='Remove Previous Registration'
                    onClick={() => fields.remove(index)}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}

            <Grid size={2}>
              <Field name={`${fieldName}.includeExcludeAction`} component={renderCriteriaOptions} type='select' />
            </Grid>

            <Grid size={3}>
              <Field
                name={`${fieldName}.domains`}
                component={renderMultiSelect}
                type='select'
                textField={(item) =>
                  item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`
                }
                dataKey='name'
                data={domainsForPreviousRegReq}
                busy={isLoading}
                placeholder='Select Domains'
                groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Domains</span>)}
              />
            </Grid>

            <Grid size={3}>
              <Field
                name={`${fieldName}.affiliates`}
                component={renderMultiSelect}
                type='select'
                textField={(item) =>
                  item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`
                }
                dataKey='name'
                data={affiliatesForPreviousRegReq}
                busy={isLoading}
                placeholder='Select Affiliates'
                groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Affiliates</span>)}
              />
            </Grid>

            <Grid size={2}>
              <Field
                name={`${fieldName}.previousRegistrationsMin`}
                component={renderField}
                maxLength={2}
                normalize={validateWholeNums}
                placeholder='min'
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }}>
              <Field
                name={`${fieldName}.previousRegistrationsMax`}
                component={renderField}
                maxLength={2}
                normalize={validateWholeNums}
                placeholder='Max'
              />
            </Grid>
          </Grid>

          {index === fields.length - 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Tooltip title='Add Previous Registration'>
                <IconButton
                  color='success'
                  aria-label='Add Previous Registration'
                  onClick={() =>
                    fields.push({
                      domains: [],
                      affiliates: [],
                      includeExcludeAction: null,
                      previousRegistrationsMin: null,
                      previousRegistrationsMax: null,
                    })
                  }
                >
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ))}
    </div>
  );
};

const PreviousRegistration = ({ domainsForPreviousRegReq, affiliatesForPreviousRegReq, isLoading }) => {
  const andOrLogicFieldVal = useSelector((state) => {
    const formSelector = state?.form?.sequenceDetails ? sequenceDetailsSelector : getFormSelector();
    if (!formSelector) {
      console.warn('Form selector not found in state');
      return {};
    }
    return formSelector(state, 'previousRegistrationConfiguration.andOrLogic');
  });

  return (
    <div>
      <Grid container spacing={2} sx={{ alignItems: 'start' }}>
        <Grid size={2}>
          <Field name='previousRegistrationConfiguration.andOrLogic' component={renderAndOrSelect} type='select' />
        </Grid>
        <Grid size={10}>
          <FieldArray
            name='previousRegistrationConfiguration.previousRegistrations'
            component={renderRegistrationFields}
            props={{
              andOrLogicFieldVal,
              domainsForPreviousRegReq,
              affiliatesForPreviousRegReq,
              isLoading,
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default PreviousRegistration;
